import type { DriveFileRef } from "./types";

/** Same types the chat "Files from computer" input already accepts, plus native Google Docs formats. */
const PICKER_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/json",
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
].join(",");

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(s);
  });
}

type GapiLoadFn = (
  module: string,
  options: { callback?: () => void; onerror?: (err: unknown) => void }
) => void;

type DocsViewInstance = {
  setIncludeFolders: (v: boolean) => DocsViewInstance;
  setMimeTypes: (types: string) => DocsViewInstance;
  setSelectFolderEnabled: (v: boolean) => DocsViewInstance;
};

type PickerBuilderInstance = {
  addView: (v: unknown) => PickerBuilderInstance;
  setOAuthToken: (t: string) => PickerBuilderInstance;
  setDeveloperKey: (k: string) => PickerBuilderInstance;
  setCallback: (
    cb: (data: Record<string, unknown>) => void
  ) => PickerBuilderInstance;
  build: () => { setVisible: (v: boolean) => void };
};

type PickerNamespace = {
  PickerBuilder: new () => PickerBuilderInstance;
  DocsView: new (viewId: unknown) => DocsViewInstance;
  ViewId: { DOCS: unknown };
  Response: { ACTION: string; DOCUMENTS: string };
  Action: { PICKED: string; CANCEL: string };
  Document: { ID: string; NAME: string; MIME_TYPE: string };
};

/**
 * Opens Google Picker (images + documents) for chat attachments. Requires Picker API +
 * Drive API enabled in GCP, and the Picker API key (REACT_APP_GOOGLE_API_KEY).
 */
export function openGoogleDrivePicker(options: {
  accessToken: string;
  developerKey: string;
  /** Fired right before the native Picker UI becomes visible, so callers can drop
   * their own "loading" state at the exact moment Google's own UI takes over. */
  onBeforeShow?: () => void;
}): Promise<DriveFileRef[]> {
  const { accessToken, developerKey, onBeforeShow } = options;

  return new Promise((resolve, reject) => {
    void (async () => {
      try {
        await loadScript("https://apis.google.com/js/api.js");
        const gapi = (window as unknown as { gapi?: { load: GapiLoadFn } }).gapi;
        if (!gapi?.load) {
          reject(new Error("Google API client (gapi) failed to load"));
          return;
        }

        await new Promise<void>((res, rej) => {
          gapi.load("picker", {
            callback: () => res(),
            onerror: () => rej(new Error("gapi failed to load picker module")),
          });
        });

        const googleWin = (window as unknown as {
          google?: { picker?: unknown };
        }).google;
        const pickerNs = googleWin?.picker as PickerNamespace | undefined;

        if (!pickerNs?.PickerBuilder) {
          reject(new Error("Google Picker API is not available"));
          return;
        }

        const { PickerBuilder, DocsView, ViewId, Response, Action, Document } =
          pickerNs;

        const docsView = new DocsView(ViewId.DOCS)
          .setIncludeFolders(false)
          .setSelectFolderEnabled(false)
          .setMimeTypes(PICKER_MIME_TYPES);

        const picker = new PickerBuilder()
          .setDeveloperKey(developerKey)
          .setOAuthToken(accessToken)
          .addView(docsView)
          .setCallback((data: Record<string, unknown>) => {
            const action = data[Response.ACTION];
            if (action === Action.PICKED) {
              const docs = (data[Response.DOCUMENTS] as Record<string, string>[]) || [];
              resolve(
                docs.map((d) => ({
                  id: d[Document.ID],
                  name: d[Document.NAME],
                  mimeType: d[Document.MIME_TYPE],
                }))
              );
            } else if (action === Action.CANCEL) {
              resolve([]);
            }
          })
          .build();
        onBeforeShow?.();
        picker.setVisible(true);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    })();
  });
}
